import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { DatePicker } from '@/components/ui/date-picker';
import { MultiSelect } from '@/components/ui/multiselect';
import { ScrollArea, ScrollAreaViewport } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import type { ReportFilterOptions } from '@/hooks/useReportFilterOptions';
import type { AuditTrailFiltersState } from '@/hooks/AuditTrail/useAuditTrailFilters';

interface AuditTrailFilterSheetProps {
  filters: AuditTrailFiltersState;
  filterOptions: ReportFilterOptions;
}

export const AuditTrailFilterSheet: React.FC<AuditTrailFilterSheetProps> = ({ filters, filterOptions }) => {
  const {
    workingFilters,
    setWorkingFilters,
    showFilterSheet,
    setShowFilterSheet,
    handleApplySheet,
  } = filters;
  const employeeOptions = filterOptions.employees.map((e) => ({ value: e.id.toString(), label: e.name }));
  const departmentOptions = filterOptions.departments.map((d) => ({ value: d, label: d }));
  const projectOptions = filterOptions.projects.map((p) => ({ value: p.id, label: p.name }));

  return (
    <Sheet open={showFilterSheet} onOpenChange={setShowFilterSheet}>
      <SheetContent side="right" className="w-full sm:w-[400px] sm:max-w-none flex flex-col p-0 gap-0">
        <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
          <SheetTitle className="text-base font-semibold">Filters</SheetTitle>
        </SheetHeader>
        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full">
            <div className="px-6 py-5 space-y-5">
              {/* Employee */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Employee</Label>
                <MultiSelect
                  options={employeeOptions}
                  value={(workingFilters.employeeIds ?? []).map(String)}
                  onValueChange={(vals) =>
                    setWorkingFilters((f) => ({ ...f, employeeIds: vals.length ? vals.map(Number) : undefined }))
                  }
                  placeholder="All employees"
                  maxDisplay={2}
                  showSearch
                />
              </div>

              {/* Date range */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Week Ending — From</Label>
                  <DatePicker
                    date={workingFilters.dateFrom}
                    onDateChange={(d) => setWorkingFilters((f) => ({ ...f, dateFrom: d }))}
                    placeholder="Pick a date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Week Ending — To</Label>
                  <DatePicker
                    date={workingFilters.dateTo}
                    onDateChange={(d) => setWorkingFilters((f) => ({ ...f, dateTo: d }))}
                    placeholder="Pick a date"
                    disabled={(date) => (workingFilters.dateFrom ? date < workingFilters.dateFrom : false)}
                  />
                </div>
              </div>

              {/* Status */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Status</Label>
                <MultiSelect
                  options={[
                    { value: 'submitted', label: 'Submitted' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'rejected', label: 'Rejected' },
                    { value: 'recalled', label: 'Recalled' },
                  ]}
                  value={workingFilters.statuses ?? []}
                  onValueChange={(vals) =>
                    setWorkingFilters((f) => ({ ...f, statuses: vals.length ? vals : undefined }))
                  }
                  placeholder="All statuses"
                  maxDisplay={2}
                  showSearch={false}
                />
              </div>

              {/* Project */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Project</Label>
                <MultiSelect
                  options={projectOptions}
                  value={workingFilters.projectIds ?? []}
                  onValueChange={(vals) =>
                    setWorkingFilters((f) => ({ ...f, projectIds: vals.length ? vals : undefined }))
                  }
                  placeholder="All projects"
                  maxDisplay={2}
                  showSearch
                />
              </div>

              {/* Department */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Department</Label>
                <MultiSelect
                  options={departmentOptions}
                  value={workingFilters.departments ?? []}
                  onValueChange={(vals) =>
                    setWorkingFilters((f) => ({ ...f, departments: vals.length ? vals : undefined }))
                  }
                  placeholder="All departments"
                  maxDisplay={2}
                  showSearch={false}
                />
              </div>

              <Separator />

              {/* Toggles */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sheet-billable-only"
                    checked={workingFilters.billableOnly ?? false}
                    onCheckedChange={(checked) =>
                      setWorkingFilters((f) => ({ ...f, billableOnly: checked === true ? true : undefined }))
                    }
                  />
                  <Label htmlFor="sheet-billable-only" className="text-sm cursor-pointer">Billable Only</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sheet-rejected-only"
                    checked={workingFilters.rejectedOnly ?? false}
                    onCheckedChange={(checked) =>
                      setWorkingFilters((f) => ({ ...f, rejectedOnly: checked === true ? true : undefined }))
                    }
                  />
                  <Label htmlFor="sheet-rejected-only" className="text-sm cursor-pointer">Rejected Only</Label>
                </div>
              </div>
            </div>
          </ScrollAreaViewport>
        </ScrollArea>
        <SheetFooter className="px-6 py-4 border-t flex-shrink-0 flex-row gap-2">
          <Button onClick={handleApplySheet} className="flex-1">Apply Filters</Button>
          <Button variant="outline" onClick={() => setWorkingFilters({})}>Clear</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
